import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, LOGO } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { Seo } from "@/components/Seo";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.next });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-gold";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-cream-light" data-testid="reset-password-page">
      <Seo title="Reset password" description="Choose a new password for your BTMEDU account." />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="bg-white rounded-2xl px-4 py-3 inline-flex shadow-sm">
            <img src={LOGO} alt="BTMEDU" className="h-10 w-auto" />
          </span>
          <h1 className="font-display text-2xl font-bold text-navy mt-6">Choose a new password</h1>
        </div>
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200/80">
          {!token ? (
            <p className="text-sm text-slate-500 text-center" data-testid="reset-no-token">
              This reset link is missing its token. Please use the link from your email, or{" "}
              <Link to="/auth" className="text-brand font-semibold hover:underline">request a new one</Link>.
            </p>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <p className="text-sm text-slate-600 mb-5">Your password has been changed. You can sign in with it now.</p>
              <Link to="/auth" className="inline-block h-12 leading-[3rem] px-8 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors" data-testid="reset-signin-link">
                Sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <PasswordInput required minLength={6} placeholder="New password (min 6 characters)"
                value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
                className={inputCls} data-testid="reset-new-password" />
              <PasswordInput required minLength={6} placeholder="Confirm new password"
                value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className={inputCls} data-testid="reset-confirm-password" />
              <button type="submit" disabled={busy}
                className="w-full h-12 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-white font-bold text-sm transition-colors"
                data-testid="reset-submit">
                {busy ? "Saving…" : "Set new password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
